import { useCallback, useEffect, useState } from 'react';
import { Box, Button, Stack, Tab, Tabs, Typography } from '@mui/material';

import Overview from './Overview';
import Runs from './Runs';
import Documents from './Documents';
import PipelineEditor from './editor/PipelineEditor';
import NewPipelineDialog from './components/NewPipelineDialog';

type View = 'overview' | 'editor' | 'runs' | 'documents';

// Clients → "Data Integrations". Rendered only for a Super Admin (useLegalAccess); every
// data-integrations endpoint re-checks that server-side.
export default function DataIntegrationsTab({ clientUid }: { clientUid: string }) {
  const [view, setView] = useState<View>('overview');
  const [pipelineUid, setPipelineUid] = useState<string | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [newOpen, setNewOpen] = useState(false);

  // Another client was picked in the list: start over on its overview.
  useEffect(() => {
    setView('overview');
    setPipelineUid(null);
  }, [clientUid]);

  const refresh = useCallback(() => setRefreshKey((k) => k + 1), []);

  const openPipeline = useCallback((uid: string) => {
    setPipelineUid(uid);
    setView('editor');
  }, []);

  const backToOverview = useCallback(() => {
    setView('overview');
    setRefreshKey((k) => k + 1);
  }, []);

  return (
    <Box sx={{ p: 3 }}>
      <Tabs
        value={view}
        onChange={(_e, v: View) => setView(v)}
        aria-label="Data Integrations"
        sx={{
          mb: 3,
          borderBottom: 1,
          borderColor: 'divider',
          minHeight: 40,
          '& .MuiTab-root': { textTransform: 'none', minHeight: 40, fontWeight: 600 }
        }}
      >
        <Tab label="Overview" value="overview" />
        <Tab label="Pipeline editor" value="editor" />
        <Tab label="Runs" value="runs" />
        <Tab label="Documents" value="documents" />
      </Tabs>

      {view === 'overview' && (
        <Overview
          clientUid={clientUid}
          refreshKey={refreshKey}
          onOpenPipeline={openPipeline}
          onOpenDocuments={() => setView('documents')}
        />
      )}

      {view === 'editor' &&
        (pipelineUid ? (
          <PipelineEditor key={pipelineUid} clientUid={clientUid} pipelineUid={pipelineUid} onBack={backToOverview} onChanged={refresh} />
        ) : (
          <Stack alignItems="center" spacing={1.5} sx={{ py: 8, textAlign: 'center' }}>
            <Typography variant="h5">No pipeline open</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 440 }}>
              Pick a pipeline on the Overview tab to edit it, or start a new one. A new pipeline is a draft until you test and publish it.
            </Typography>
            <Stack direction="row" spacing={1}>
              <Button variant="contained" onClick={() => setNewOpen(true)}>
                New pipeline
              </Button>
              <Button variant="outlined" color="secondary" onClick={() => setView('overview')}>
                All pipelines
              </Button>
            </Stack>
          </Stack>
        ))}

      {view === 'runs' && <Runs clientUid={clientUid} />}

      {view === 'documents' && <Documents clientUid={clientUid} />}

      <NewPipelineDialog
        open={newOpen}
        clientUid={clientUid}
        onClose={() => setNewOpen(false)}
        onCreated={(uid) => {
          setNewOpen(false);
          refresh();
          openPipeline(uid);
        }}
      />
    </Box>
  );
}
